import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  assignOrder,
  getOrderById,
  getOrderUserById,
  putOrder,
  removeAssignOrder,
} from "../../app/services/orderService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import Form from "react-bootstrap/Form";
import {
  Alert,
  Breadcrumb,
  Button,
  Modal,
} from "react-bootstrap";
import {
  Plus,
} from "react-bootstrap-icons";
import { Article, defaultArticle } from "../../models/responses/article";
import { defaultOrder, Order } from "../../models/responses/order";
import {
  getOrderStatusById,
  getOrderStatuses,
} from "../../app/services/orderStatusService";
import {
  setClientToggleLocked,
  setClientType,
} from "../../app/slices/clientTypeSlice";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Customer } from "../../models/responses/customer";
import { getCustomers } from "../../app/services/customerService";
import { OrderStatus } from "../../models/responses/orderStatus";
import { OrderType } from "../../models/responses/orderType";
import { getOrderTypes } from "../../app/services/orderTypeService";
import { Supplier } from "../../models/responses/supplier";
import { getSuppliers } from "../../app/services/supplierService";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { getBrandDiscountByCarBrandId } from "../../app/services/brandDiscountService";
import { getCarBrands } from "../../app/services/carBrandService";
import { getArticleTemplatesByType } from "../../app/services/articleTemplateService";
import moment from "moment";
import OrderFields from "../../components/orders/orderFields";
import CustomerInfoModal from "../../components/orders/customerInfoModal";
import EditableArticleModal, { EditableArticleModalHandlers } from "../../components/orders/editableArticleModal";
import EditableProductTable, { EditableProductTableHandlers } from "../../components/orders/editableProductTable";
import EditableTimeTable, { EditableTimeTableHandlers } from "../../components/orders/editableTimeTable";
import { useArticleTableHandlers, useArticleModalHandlers } from "../../app/hooks/useArticleHandlers";

interface Props { }

const OrderEdit: FC<Props> = () => {
  moment.locale("sv");
  moment.updateLocale(moment.locale(), { invalidDate: "-" });

  // Router
  const { orderId } = useParams<{ orderId: string }>();
  const { orderStatusId } = useParams<{ orderStatusId: string }>();
  const navigate = useNavigate();

  // Auth
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  // Redux
  const dispatch = useAppDispatch();
  const order = useSelector((state: RootState) => state.order.order);
  const orderUser = useSelector((state: RootState) => state.order.orderUser);
  const orderStatus: OrderStatus = useSelector((state: RootState) => state.orderStatus.orderStatus);
  const orderStatusList: OrderStatus[] = useSelector((state: RootState) => state.orderStatus.setOrderStatusList);
  const clientType = useSelector((state: RootState) => state.clientType.clientType);
  const customers: Customer[] = useSelector((state: RootState) => state.customer.customerList);
  const orderTypes: OrderType[] = useSelector((state: RootState) => state.orderTypes.orderTypeList);
  const suppliers: Supplier[] = useSelector((state: RootState) => state.supplier.supplierList);
  const carBrands = useSelector((state: RootState) => state.carBrand.carBrandList);
  const brandDiscount = useSelector((state: RootState) => state.brandDiscount.brandDiscount);
  const articleTemplateListTime = useSelector((state: RootState) => state.articleTemplate.articleTemplateListTime);
  const articleTemplateListProduct = useSelector((state: RootState) => state.articleTemplate.articleTemplateListProduct);
  const isWaitingOrderPostResponse = useSelector((state: RootState) => state.order.isWaitingOrderPostResponse);
  const isLoadingOrderStatuses = useSelector((state: RootState) => state.orderStatus.isWaitingOrderStatusGetResponse);
  const isLoadingOrderTypes = useSelector((state: RootState) => state.orderTypes.isWaitingOrderTypeGetResponse);
  const isLoadingSuppliers = useSelector((state: RootState) => state.supplier.isWaitingSupplierGetResponse);
  const isLoadingCustomers = useSelector((state: RootState) => state.customer.isWaitingCustomerGetResponse);
  const isLoadingCarBrands = useSelector((state: RootState) => state.carBrand.isWaitingCarBrandGetResponse);

  // Local state
  const [orderData, setOrderData] = useState<Order>({ ...defaultOrder });
  const [modalArticle, setModalArticle] = useState<Article>({ ...defaultArticle });
  const [showAssignedModal, setShow] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [articleErrorMessage, setArticleErrorMessage] = useState<string>("");
  const [allProductVerified, setAllProductVerified] = useState(false);
  const [allTimeVerified, setAllTimeVerified] = useState(false);
  const [displayError, setDisplayError] = useState(false);

  // Article handlers from hooks
  const articleTableHandlers = useArticleTableHandlers({
    orderData,
    setOrderData,
    brandDiscount,
  });

  const articleModalHandlers = useArticleModalHandlers({
    orderData,
    setOrderData,
    modalArticle,
    setModalArticle,
    setShowArticleModal,
    brandDiscount,
    suppliers,
  });

  // Effects - Initial data loading
  useEffect(() => {
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Time));
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Product));
  }, [account, dispatch, instance]);

  useEffect(() => {
    dispatch(getCarBrands(instance, account));
  }, [instance, account, dispatch]);

  useEffect(() => {
    dispatch(setClientToggleLocked(true));
    dispatch(getOrderById(instance, account, orderId));
  }, [account, accounts, dispatch, instance, orderId]);

  useEffect(() => {
    dispatch(assignOrder(instance, account, orderId));
  }, [dispatch, instance, account, orderId]);

  useEffect(() => {
    dispatch(getBrandDiscountByCarBrandId(instance, account, orderData?.selectedCarBrandId || ""));
  }, [dispatch, instance, account, orderData?.selectedCarBrandId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      dispatch(getOrderUserById(instance, account, orderId));
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [account, accounts, dispatch, instance, orderStatusId]);

  useEffect(() => {
    dispatch(getOrderStatuses(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getCustomers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getOrderTypes(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getSuppliers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  // Effects - Data synchronization
  useEffect(() => {
    orderData.userId = orderUser.userId;
  }, [orderUser]);

  useEffect(() => {
    if (orderData.userId && account?.localAccountId) {
      setShow(orderData.userId !== account.localAccountId);
    }
  }, [orderData.userId, account?.localAccountId, orderUser.id]);

  useEffect(() => {
    if (order && order.id) {
      setOrderData({ ...order });
      if (clientType !== order.clientType) {
        dispatch(setClientType(order.clientType));
      }
    }
  }, [order, clientType, dispatch]);

  useEffect(() => {
    checkVerified();
  }, [orderData.articles]);

  useEffect(() => {
    setModalArticle({
      ...modalArticle,
      totalExVat: parseFloat(
        (parseFloat(modalArticle.salesPrice) * modalArticle.quantity).toFixed(2),
      ),
    });
  }, [modalArticle.quantity, modalArticle.salesPrice, modalArticle.vat]);

  useEffect(() => {
    orderData.articles?.forEach((article) => {
      articleTableHandlers.onSalesPriceChange(article.rowNumber, article.salesPrice);
      articleModalHandlers.onSalesPriceChange({
        target: { value: article.salesPrice },
      } as React.ChangeEvent<HTMLInputElement>);
    });
  }, [brandDiscount]);

  // ORDER HANDLERS
  const customerInputHandler = (e: string) => {
    setOrderData({ ...orderData, customerId: e });
  };
  const assignedToInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, assignedTo: e.target.value });
  };
  const registrationNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, registrationNumber: e.target.value });
  };
  const vinInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, vin: e.target.value });
  };
  const carInfoInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, carInfo: e.target.value });
  };
  const aoNumberInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, aoNumber: e.target.value });
  };
  const salesOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, salesOrderNumber: e.target.value });
  };
  const purchaseOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, purchaseOrderNumber: e.target.value });
  };
  const orderTypeInputHandler = (e: string) => {
    if (e === "") {
      setOrderData({ ...orderData, orderTypeId: null });
    } else {
      const selectedType = orderTypes.find((type) => type.id === e);
      const newDiscount = selectedType?.discount ?? 0;
      setOrderData({
        ...orderData,
        orderTypeId: e,
        discount: newDiscount,
        articles:
          orderData.articles?.map((article) =>
            article.verified ? article : { ...article, discount: newDiscount },
          ) ?? [],
      });
    }
  };
  const orderedByInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, orderedBy: e.target.value });
  };
  const followUpInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, followUp: e.target.value });
  };
  const emailBodyInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, emailBody: e.target.value });
  };
  const warehouseLocationInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, warehouseLocation: e.target.value });
  };
  const supplierInputHandler = (e: string | null) => {
    if (e === "") e = null;

    if (orderData.articles) {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((article) => {
          if (article.supplierId === null) {
            return { ...article, supplierId: e };
          }
          return article;
        }),
        supplierId: e,
      });
    }
  };
  const arriveDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, arriveDate: e.target.value });
  };
  const bookingDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, bookingDate: e.target.value });
  };
  const deliveryDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, deliveryDate: e.target.value });
  };
  const pickupDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, pickupDate: e.target.value });
  };
  const orderDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, orderDate: e.target.value });
  };
  const filenameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, filename: e.target.value });
  };
  const commentInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, comment: e.target.value });
  };
  const internalCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, internalComment: e.target.value });
  };
  const priceProposalMessageInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, priceProposalMessage: e.target.value });
  };
  const orderApprovedInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, approved: e.target.checked });
  };
  const orderDeniedInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, denied: e.target.checked });
  };
  const orderClientExternalOrderIdHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOrderData({ ...orderData, clientExternalOrderId: e.target.value });
  };
  const orderStatusInputHandler = (e: string) => {
    setOrderData({ ...orderData, orderStatusId: e });
  };
  const selectedCarBrandInputHandler = (e: string) => {
    if (e === "") {
      setOrderData({ ...orderData, selectedCarBrandId: null });
    } else {
      const selectedBrand = carBrands.find((brand) => brand.id === e);
      setOrderData({
        ...orderData,
        selectedCarBrandId: selectedBrand ? selectedBrand.id : null,
      });
    }
  };

  // ARTICLES - Complex handlers that must stay here (rest is in useArticleHandlers)
  const articleListValidatedHandler = (articleRow: number) => {
    const article = orderData.articles?.find(
      (art) => art.rowNumber === articleRow,
    );
    let errorFields: string[] = [];
    if (!article) {
      errorFields.push("Artikeln hittades inte");
    } else if (!article.verified) {
      if (clientType === ClientType.Fleet && !article.articleNumber) {
        errorFields.push("Artikelnummer saknas");
      }
      if (clientType !== ClientType.Fleet && article.articleType === ArticleType.Product) {
        if (!article.purchasePrice || parseFloat(article.purchasePrice) <= 0) {
          if (clientType === ClientType.Standard && article.quantity > 0) {
            errorFields.push("Inköpspris måste vara mer än 0");
          }
        }
      }
      if (article.articleType === ArticleType.Time && (article.time == null || article.time < 1)) {
        setPendingQuantityRows([articleRow]);
        setShowArticleQuantityErrorModal(true);
        return;
      }
      if (article.articleType !== ArticleType.Time && (article.quantity == null || article.quantity < 1)) {
        setPendingQuantityRows([articleRow]);
        setShowArticleQuantityErrorModal(true);
        return;
      }
    }
    if (errorFields.length > 0) {
      setArticleErrorMessage("Kontrollera: " + errorFields.join(", "));
      setShowArticleErrorModal(true);
      return;
    }

    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((art) =>
        art.rowNumber === articleRow
          ? { ...art, verified: !art.verified }
          : art,
      ),
    });
  };

  const addNewArticleHandler = () => {
    let newArticle: Article = { ...defaultArticle };
    newArticle.discount = orderData.discount;
    newArticle.rowNumber = calculateRowNumber();
    newArticle.articleType = ArticleType.Product;

    if (orderData.articles === null) return;

    let newList = [...orderData.articles, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const addNewTimeArticleHandler = () => {
    let newArticle: Article = JSON.parse(JSON.stringify(defaultArticle));
    newArticle.rowNumber = calculateRowNumber();
    newArticle.articleType = ArticleType.Time;

    let articlesList = Array.isArray(orderData.articles)
      ? orderData.articles
      : [];
    let newList = [...articlesList, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const calculateRowNumber = () => {
    if (!orderData.articles || orderData.articles.length === 0) return 0;

    return (Math.max(...orderData.articles!.map((a) => a.rowNumber)) ?? 0) + 1;
  };

  const [showArticleErrorModal, setShowArticleErrorModal] = useState(false);
  const [showArticleQuantityErrorModal, setShowArticleQuantityErrorModal] = useState(false);
  const [pendingQuantityRows, setPendingQuantityRows] = useState<number[]>([]);

  const toggleAllArticlesVerified = (articleType: ArticleType) => {
    const newVerifiedValue =
      articleType === ArticleType.Product
        ? !allProductVerified
        : !allTimeVerified;

    let errorFields: string[] = [];
    const lowQuantityRows: number[] = [];

    const updatedArticles =
      orderData.articles?.map((article) => {
        if (article.articleType !== articleType) return article;
        if (newVerifiedValue) {
          if (article.articleType === ArticleType.Time && (article.time == null || article.time < 1)) {
            lowQuantityRows.push(article.rowNumber);
            return article;
          }
          if (article.articleType !== ArticleType.Time && (article.quantity == null || article.quantity < 1)) {
            lowQuantityRows.push(article.rowNumber);
            return article;
          }
          if (clientType === ClientType.Fleet && !article.articleNumber) {
            errorFields.push(
              `Rad ${article.rowNumber}: Artikelnummer saknas (Fleet)`,
            );
            return { ...article, verified: false };
          }
          if (clientType !== ClientType.Fleet && article.articleType === ArticleType.Product) {
            if (
              !article.purchasePrice ||
              parseFloat(article.purchasePrice) <= 0
            ) {
              errorFields.push(
                `Rad ${article.rowNumber}: Inköpspris måste vara mer än 0 (Produkt)`,
              );
              return { ...article, verified: false };
            }
          }
        }
        return { ...article, verified: newVerifiedValue };
      }) ?? [];

    if (errorFields.length > 0) {
      setArticleErrorMessage("Fel: " + errorFields.join(", "));
      setShowArticleErrorModal(true);
    }
    setOrderData({
      ...orderData,
      articles: updatedArticles,
    });

    if (lowQuantityRows.length > 0) {
      setPendingQuantityRows(lowQuantityRows);
      setShowArticleQuantityErrorModal(true);
    }
  };

  const handleConfirmQuantityOverride = () => {
    setShowArticleQuantityErrorModal(false);
    if (pendingQuantityRows.length > 0) {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((art) =>
          pendingQuantityRows.includes(art.rowNumber) ? { ...art, verified: true } : art,
        ),
      });
      setPendingQuantityRows([]);
    }
  };

  const articleTemplateHandler = (
    rowNumber: number,
    templateId: string,
    type: ArticleType,
  ) => {
    const list =
      type === ArticleType.Time
        ? articleTemplateListTime
        : articleTemplateListProduct;

    if (!templateId) {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((article) =>
          article.rowNumber === rowNumber
            ? {
              ...article,
              articleTemplateId: null,
              articleNumber: "",
              supplierId: null,
              purchasePrice: "0",
            }
            : article,
        ),
      });
      return;
    }

    const template = list.find((t) => t.id === templateId);
    if (!template) return;

    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            articleTemplateId: template.id,
            salesPrice: template.keepIncomingPrice
              ? article.salesPrice
              : template.salesPrice,
            articleNumber: template.articleNumber,
            supplierId: null,
            purchasePrice: "0",
          }
          : article,
      ),
    });
  };

  const checkVerified = () => {
    const productArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Product,
      ) ?? [];
    const timeArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Time,
      ) ?? [];
    setAllProductVerified(
      productArticles.length > 0 &&
      productArticles.every((art) => art.verified),
    );
    setAllTimeVerified(
      timeArticles.length > 0 && timeArticles.every((art) => art.verified),
    );
  };

  const [showAlreadyExistsModal, setShowAlreadyExistsModal] = useState(false);
  const [pendingOverrideOrder, setPendingOverrideOrder] =
    useState<Order | null>(null);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = useState<string>("");

  const handleUpdateOrder = () => {
    setDisplayError(false);
    dispatch(
      putOrder(
        instance,
        account,
        orderData,
        onSuccess,
        (errorType, errorMessage) => {
          if (errorType === "ALREADY_EXISTS") {
            setAlreadyExistsMessage(
              errorMessage ||
              "Det finns redan en order med detta PostNord-id i systemet.",
            );
            setPendingOverrideOrder({ ...orderData });
            setShowAlreadyExistsModal(true);
          } else {
            setDisplayError(true);
          }
        },
      ),
    );
  };

  const handleOverrideOrder = () => {
    if (pendingOverrideOrder) {
      const overrideOrder = {
        ...pendingOverrideOrder,
        overrideException: true,
      };
      setShowAlreadyExistsModal(false);
      setPendingOverrideOrder(null);
      dispatch(
        putOrder(
          instance,
          account,
          overrideOrder,
          onSuccess,
          (errorType, errorMessage) => {
            setDisplayError(true);
          },
        ),
      );
    }
  };

  const handleCancelOverride = () => {
    setShowAlreadyExistsModal(false);
    setPendingOverrideOrder(null);
  };

  const cancelUpdateOrder = () => {
    dispatch(removeAssignOrder(instance, account, orderId));
    navigateToViewPage();
  };

  const handleAssignOrder = () => {
    dispatch(assignOrder(instance, account, orderId));
    setShow(false);
  };

  const onSuccess = (orderId: string | null) => {
    if (orderId === null) {
      setDisplayError(true);
      return;
    }
    navigateToViewPage();
  };

  const navigateToViewPage = () => {
    setTimeout(() => {
      navigate(
        `/orderStatus/${orderData.orderStatusId}/orders/view/${orderData.id}`,
        { relative: "path" },
      );
    }, 500);
  };

  // OrderFields props
  const orderFieldsHandlers = {
    onStatusChange: orderStatusInputHandler,
    onCustomerChange: customerInputHandler,
    onAssignedToChange: assignedToInputHandler,
    onRegistrationNumberChange: registrationNumberInputHandler,
    onCarBrandChange: selectedCarBrandInputHandler,
    onVinChange: vinInputHandler,
    onCarInfoChange: carInfoInputHandler,
    onAoNumberChange: aoNumberInputHandler,
    onPurchaseOrderNumberChange: purchaseOrderNumberInputHandler,
    onOrderTypeChange: orderTypeInputHandler,
    onDiscountChange: (value: number | null) => {
      setOrderData({
        ...orderData,
        discount: value,
        articles:
          orderData.articles?.map((article) =>
            article.verified ? article : { ...article, discount: value },
          ) ?? [],
      });
    },
    onOrderedByChange: orderedByInputHandler,
    onFollowUpChange: followUpInputHandler,
    onEmailBodyChange: emailBodyInputHandler,
    onWarehouseLocationChange: warehouseLocationInputHandler,
    onSupplierChange: supplierInputHandler,
    onArriveDateChange: arriveDateInputHandler,
    onBookingDateChange: bookingDateInputHandler,
    onDeliveryDateChange: deliveryDateInputHandler,
    onPickupDateChange: pickupDateInputHandler,
    onOrderDateChange: orderDateInputHandler,
    onFilenameChange: filenameInputHandler,
    onCommentChange: commentInputHandler,
    onInternalCommentChange: internalCommentInputHandler,
    onPriceProposalMessageChange: priceProposalMessageInputHandler,
    onApprovedChange: orderApprovedInputHandler,
    onDeniedChange: orderDeniedInputHandler,
    onClientExternalOrderIdChange: orderClientExternalOrderIdHandler,
  };

  const orderFieldsLists = {
    orderStatusList,
    customers,
    suppliers,
    orderTypes,
    carBrands,
  };

  const orderFieldsLoading = {
    isLoadingOrderStatuses,
    isLoadingCustomers,
    isLoadingSuppliers,
    isLoadingOrderTypes,
    isLoadingCarBrands,
  };

  // Article modal handlers using hook
  const editableArticleModalHandlers: EditableArticleModalHandlers = {
    onArticleNumberChange: articleModalHandlers.onArticleNumberChange,
    onCommentChange: articleModalHandlers.onCommentChange,
    onDeliveredQuantityChange: articleModalHandlers.onDeliveredQuantityChange,
    onDescriptionChange: articleModalHandlers.onDescriptionChange,
    onDiscountChange: articleModalHandlers.onDiscountChange,
    onExternalArticleCodeChange: articleModalHandlers.onExternalArticleCodeChange,
    onInternalCommentChange: articleModalHandlers.onInternalCommentChange,
    onPurchaseOrderNumberChange: articleModalHandlers.onPurchaseOrderNumberChange,
    onPurchasePriceChange: articleModalHandlers.onPurchasePriceChange,
    onQuantityChange: articleModalHandlers.onQuantityChange,
    onRemove: articleModalHandlers.onRemove,
    onReviewedQuoteChange: articleModalHandlers.onReviewedQuoteChange,
    onSalesPriceChange: articleModalHandlers.onSalesPriceChange,
    onSave: articleModalHandlers.onSave,
    onSupplierChange: articleModalHandlers.onSupplierChange,
    onValidatedChange: articleModalHandlers.onValidatedChange,
  };

  // Product table handlers using hook
  const productTableHandlers: EditableProductTableHandlers = {
    onArticleClick: articleModalHandlers.onOpen,
    onArticleNumberChange: articleTableHandlers.onArticleNumberChangeWithTemplateClear,
    onArticleTemplateChange: (rowNumber, templateId) =>
      articleTemplateHandler(rowNumber, templateId, ArticleType.Product),
    onCommentChange: articleTableHandlers.onCommentChange,
    onDescriptionChange: articleTableHandlers.onDescriptionChange,
    onDiscountChange: articleTableHandlers.onDiscountChange,
    onPurchaseOrderNumberChange: articleTableHandlers.onPurchaseOrderNumberChange,
    onPurchasePriceChange: articleTableHandlers.onPurchasePriceChange,
    onQuantityChange: articleTableHandlers.onQuantityChange,
    onSalesPriceChange: articleTableHandlers.onSalesPriceChange,
    onSupplierChange: articleTableHandlers.onSupplierChange,
    onToggleAllVerified: () => toggleAllArticlesVerified(ArticleType.Product),
    onValidatedChange: articleListValidatedHandler,
  };

  // Time table handlers using hook
  const timeTableHandlers: EditableTimeTableHandlers = {
    onArticleNumberChange: articleTableHandlers.onArticleNumberChangeWithTemplateClear,
    onArticleTemplateChange: (rowNumber, templateId) =>
      articleTemplateHandler(rowNumber, templateId, ArticleType.Time),
    onDescriptionChange: articleTableHandlers.onDescriptionChange,
    onQuantityChange: articleTableHandlers.onQuantityChange,
    onRemove: (rowNumber) => {
      const keptArticles = orderData.articles?.filter((a) => a.rowNumber !== rowNumber);
      if (keptArticles) {
        setOrderData({ ...orderData, articles: keptArticles });
      }
    },
    onSalesPriceChange: articleTableHandlers.onSalesPriceChange,
    onTimeChange: articleTableHandlers.onTimeChange,
    onToggleAllVerified: () => toggleAllArticlesVerified(ArticleType.Time),
    onValidatedChange: articleListValidatedHandler,
  };

  return (
    <div>
      <Modal
        show={showAlreadyExistsModal}
        onHide={handleCancelOverride}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Varning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {alreadyExistsMessage ||
              "Det finns redan en order med detta PostNord-id i systemet. Vill du skapa den ändå?"}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelOverride}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleOverrideOrder}>
            Spara ändå
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showAssignedModal} onHide={navigateToViewPage}>
        <Modal.Header closeButton>
          <Modal.Title>Låst order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Den här ordern redigeras av {orderData.userName}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={navigateToViewPage}>
            Stäng
          </Button>
          <Button variant="primary" onClick={handleAssignOrder}>
            Lås upp
          </Button>
        </Modal.Footer>
      </Modal>
      <Breadcrumb>
        <Breadcrumb.Item href={`/orderStatus/${orderStatus.id}`}>
          {orderStatus.name}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Uppdatera order</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form>
        <OrderFields
          orderData={orderData}
          orderStatus={orderStatus}
          clientType={clientType}
          order={order}
          onShowCustomerModal={() => setShowCustomerModal(true)}
          mode="edit"
          wrapInForm={false}
          handlers={orderFieldsHandlers}
          lists={orderFieldsLists}
          loadingStates={orderFieldsLoading}
        />
        <CustomerInfoModal
          show={showCustomerModal}
          customer={order.customer}
          onClose={() => setShowCustomerModal(false)}
        />
        <EditableProductTable
          articles={orderData.articles || []}
          allProductVerified={allProductVerified}
          clientType={clientType}
          orderPageType={orderStatus.orderPageType}
          suppliers={suppliers}
          orderSupplierId={orderData.supplierId}
          articleTemplates={articleTemplateListProduct}
          handlers={productTableHandlers}
        />
        <EditableArticleModal
          show={showArticleModal}
          article={modalArticle}
          clientType={clientType}
          suppliers={suppliers}
          isExported={orderData.exported}
          isLoadingSuppliers={isLoadingSuppliers}
          handlers={editableArticleModalHandlers}
          onHide={articleModalHandlers.onClose}
        />
        <Button
          variant="outline-primary"
          className="mb-3"
          onClick={addNewArticleHandler}
        >
          Lägg till artikel <Plus />
        </Button>

        {clientType === ClientType.Fleet && (
          <EditableTimeTable
            articles={orderData.articles || []}
            allTimeVerified={allTimeVerified}
            isExported={orderData.exported}
            articleTemplates={articleTemplateListTime}
            handlers={timeTableHandlers}
          />
        )}
        {clientType === ClientType.Fleet && (
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={addNewTimeArticleHandler}
          >
            Lägg till reparationstid <Plus />
          </Button>
        )}
        <hr />

        <div>
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={cancelUpdateOrder}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            className="mb-3 float-end"
            onClick={handleUpdateOrder}
            disabled={isWaitingOrderPostResponse}
          >
            Uppdatera order
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>Något gick fel, kontrollera uppgifterna och försök igen.</p>
        </Alert>
      </Form>
      <Modal
        show={showArticleErrorModal}
        onHide={() => setShowArticleErrorModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fel vid klarmarkering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {articleErrorMessage ||
            "Du måste ange både inköpspris och försäljningspris (ej 0) innan du kan klarmarkera artikeln."}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowArticleErrorModal(false)}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showArticleQuantityErrorModal}
        onHide={() => {
          setShowArticleQuantityErrorModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Fel vid klarmarkering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Är du säker på att du vill klarmarkera? Angivet antal är 0 på en eller flera artiklar.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowArticleQuantityErrorModal(false);
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmQuantityOverride}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderEdit;
